import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { JourneyPhase, PhaseStatus } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { listJourneyPhases } from "@/lib/journey-phases-api";

// TES-209 — clinical journey-phase surface. A flat ordered list of named phases
// the patient progresses through; per-patient progress is derived from existing
// signals (no manual advancement). The editor lives at /journey-phases/:key.

const STATUS_LABELS: Record<PhaseStatus, string> = {
  draft: "Utkast",
  published: "Publicerad",
  archived: "Arkiverad",
};

function statusChip(status: PhaseStatus) {
  const cls =
    status === "published"
      ? "bg-success/10 text-success border-success/30"
      : status === "archived"
        ? "bg-surfaceMuted text-textSecondary border-surfaceMuted"
        : "bg-warning/10 text-warning border-warning/30";
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${cls}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export function JourneyPhasesListRoute() {
  const { signOut } = useAuth();
  const [rows, setRows] = useState<JourneyPhase[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listJourneyPhases()
      .then(setRows)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Faser</h1>
        <Link
          to="/journey-phases/new"
          className="rounded-md bg-brand text-white px-md py-sm text-sm font-medium hover:bg-brand/90 transition"
        >
          Ny fas
        </Link>
      </div>

      <p className="text-sm text-textSecondary max-w-2xl">
        Faser är den kliniska resan patienten går igenom (t.ex. “Lär känna din
        kropp” → “Bygg din grund”). Varje steg markeras som klart automatiskt
        utifrån patientens data — ingen manuell framflyttning. Bara publicerade
        faser visas för patienten.
      </p>

      <div className="flex items-end">
        <span className="text-sm text-textSecondary ml-auto">
          {loading ? "Hämtar…" : rows ? `${rows.length} faser` : ""}
        </span>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga faser ännu. Skapa din första med “Ny fas”.
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">Ordning</th>
                <th className="text-left px-md py-sm font-medium">Namn</th>
                <th className="text-left px-md py-sm font-medium">Nyckel</th>
                <th className="text-left px-md py-sm font-medium">Status</th>
                <th className="text-left px-md py-sm font-medium">Steg</th>
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {[...rows]
                .sort((a, b) => a.order - b.order)
                .map((row) => (
                  <tr
                    key={row.key}
                    className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                  >
                    <td className="px-md py-sm text-textSecondary">
                      {row.order}
                    </td>
                    <td className="px-md py-sm font-medium">
                      {row.emoji ? `${row.emoji} ` : ""}
                      {row.title.sv || "(utan namn)"}
                    </td>
                    <td className="px-md py-sm font-mono text-xs text-textSecondary">
                      {row.key}
                    </td>
                    <td className="px-md py-sm">{statusChip(row.status)}</td>
                    <td className="px-md py-sm text-textSecondary">
                      {row.steps.length}
                    </td>
                    <td className="px-md py-sm text-right">
                      <Link
                        to={`/journey-phases/${encodeURIComponent(row.key)}`}
                        className="text-brand hover:underline text-sm font-medium"
                      >
                        Redigera →
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
